import React, { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { bloodTestsAPI } from '../services/api'

export const MarkerInformation = ({ markerName, value, status }) => {
  const [expanded, setExpanded] = useState(false)
  const { t, i18n } = useTranslation()

  // Only fetch when expanded, include language in query key
  const { data: markerInfo, isLoading, error } = useQuery({
    queryKey: ['markerInfo', markerName, value, status, i18n.language],
    queryFn: () => bloodTestsAPI.getMarkerInfo(markerName, value, status, i18n.language),
    enabled: expanded,
    staleTime: 1000 * 60 * 30, // Cache for 30 minutes
  })

  const handleToggle = () => {
    setExpanded(!expanded)
  }

  const _getStatusColor = (markerStatus) => {
    const colors = {
      'optimal': 'text-green-600',
      'normal': 'text-green-600',
      'borderline': 'text-yellow-600',
      'slightly_abnormal': 'text-orange-500',
      'abnormal': 'text-red-500',
      'critical': 'text-red-700',
    }
    return colors[markerStatus?.toLowerCase()] || 'text-gray-600'
  }

  return (
    <div className="marker-information">
      {/* Toggle Button */}
      <button
        onClick={handleToggle}
        className="inline-flex items-center text-blue-600 hover:text-blue-800 text-sm mt-1"
        aria-expanded={expanded}
      >
        <svg
          className={`w-4 h-4 mr-1 transition-transform ${expanded ? 'rotate-90' : ''}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
        </svg>
        {expanded ? t('markerInfo.hide') : t('markerInfo.learnMore')}
      </button>

      {/* Expanded Information Panel */}
      {expanded && (
        <div className="mt-3 p-4 bg-blue-50 border border-blue-200 rounded-lg">
          {isLoading && (
            <div className="flex items-center justify-center py-4">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
              <span className="ml-2 text-blue-600">{t('markerInfo.loading')}</span>
            </div>
          )}

          {error && (
            <div className="text-red-600 p-3 bg-red-50 rounded">
              <p>{t('markerInfo.error')}</p>
            </div>
          )}

          {markerInfo?.data && markerInfo.data.source === 'database' && (
            <div className="space-y-4">
              {/* Header */}
              <div className="border-b border-blue-200 pb-3">
                <h4 className="text-lg font-semibold text-gray-900">
                  {markerInfo.data.common_name || markerInfo.data.marker_name}
                </h4>
                {markerInfo.data.abbreviation && (
                  <p className="text-sm text-gray-600">({markerInfo.data.abbreviation})</p>
                )}
              </div>

              {/* Description */}
              <div>
                <h5 className="font-medium text-gray-900 mb-1">{t('markerInfo.whatIsThis')}</h5>
                <p className="text-gray-700 text-sm">{markerInfo.data.description}</p>
              </div>

              {/* Clinical Significance */}
              {markerInfo.data.clinical_significance && (
                <div>
                  <h5 className="font-medium text-gray-900 mb-1">{t('markerInfo.whyItMatters')}</h5>
                  <p className="text-gray-700 text-sm">{markerInfo.data.clinical_significance}</p>
                </div>
              )}

              {/* Reference Ranges */}
              <div className="grid grid-cols-2 gap-3">
                {markerInfo.data.normal_range && (
                  <div className="bg-surface-container p-3 rounded border border-gray-200">
                    <p className="text-xs text-gray-600 mb-1">{t('markerInfo.normalRange')}</p>
                    <p className="font-medium text-sm">
                      {markerInfo.data.normal_range.min} - {markerInfo.data.normal_range.max}
                      {markerInfo.data.unit && <span className="text-gray-600 ml-1">{markerInfo.data.unit}</span>}
                    </p>
                  </div>
                )}
                {markerInfo.data.optimal_range && (
                  <div className="bg-green-50 p-3 rounded border border-green-200">
                    <p className="text-xs text-green-700 mb-1">{t('markerInfo.optimalRange')}</p>
                    <p className="font-medium text-sm text-green-800">
                      {markerInfo.data.optimal_range.min} - {markerInfo.data.optimal_range.max}
                      {markerInfo.data.unit && <span className="text-green-600 ml-1">{markerInfo.data.unit}</span>}
                    </p>
                  </div>
                )}
              </div>

              {/* Status-specific implications */}
              {status && status.toLowerCase() === 'low' && markerInfo.data.low_implications && (
                <div className="bg-yellow-50 border-l-4 border-yellow-400 p-3">
                  <h5 className="font-medium text-yellow-800 mb-1 flex items-center">
                    <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                    {t('markerInfo.lowValueImplications')}
                  </h5>
                  <p className="text-sm text-yellow-800">{markerInfo.data.low_implications}</p>
                </div>
              )}

              {status && ['high', 'abnormal', 'critical'].includes(status.toLowerCase()) && markerInfo.data.high_implications && (
                <div className={`border-l-4 p-3 ${
                  status.toLowerCase() === 'critical'
                    ? 'bg-red-50 border-red-500'
                    : 'bg-orange-50 border-orange-400'
                }`}>
                  <h5 className={`font-medium mb-1 flex items-center ${
                    status.toLowerCase() === 'critical' ? 'text-red-800' : 'text-orange-800'
                  }`}>
                    <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                    {t('markerInfo.highValueImplications')}
                  </h5>
                  <p className={`text-sm ${
                    status.toLowerCase() === 'critical' ? 'text-red-800' : 'text-orange-800'
                  }`}>{markerInfo.data.high_implications}</p>
                </div>
              )}

              {/* Personalized Insights from AI */}
              {markerInfo.data.personalized_insights?.text && (
                <div className="bg-purple-50 border border-purple-200 p-4 rounded-lg">
                  <h5 className="font-medium text-purple-900 mb-2 flex items-center">
                    <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                    </svg>
                    {t('markerInfo.personalizedForYou')}
                  </h5>
                  <div className="text-sm text-purple-800 whitespace-pre-line">
                    {markerInfo.data.personalized_insights.text}
                  </div>
                  <p className="text-xs text-purple-600 mt-2">
                    {t('markerInfo.aiPoweredInsights')}
                  </p>
                </div>
              )}
            </div>
          )}

          {/* AI-Generated Content (for unknown markers) */}
          {markerInfo?.data && markerInfo.data.source === 'ai_generated' && (
            <div className="space-y-3">
              <div className="flex items-start">
                <svg className="w-5 h-5 text-purple-500 mt-0.5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
                <div className="flex-1">
                  <h4 className="font-medium text-gray-900 mb-2">{markerInfo.data.marker_name}</h4>
                  <div className="text-sm text-gray-700 whitespace-pre-line prose prose-sm">
                    {markerInfo.data.full_explanation}
                  </div>
                </div>
              </div>
              <div className="bg-purple-50 p-3 rounded border border-purple-200">
                <p className="text-xs text-purple-700">
                  ⓘ {markerInfo.data.note}
                </p>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

export default MarkerInformation
